import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface DirectReceiptRequest {
  locationCode: string;
  quantity?: number;
  itemBatchNo?: string;
  itemBatchExpDate?: string;
  notes?: string;
  processedBy?: string;
}

export interface DirectReceiptResponse {
  success: boolean;
  errorMessage?: string;
  transaction?: any; // StcTransDto
  processedAt: Date;
  processedBy?: string;
  finalLocation?: string;
  /** Allokering (TW 36362298): ResID for tidlig reservasjon opprettet ved mottak av allokert PO-linje */
  allocationResID?: number;
  /** Allokering: satt hvis mottaket lyktes men reservasjonen IKKE kunne opprettes — vis til operatør */
  allocationWarning?: string;
}

export interface DirectReceiptStatusResponse {
  isProcessed: boolean;
  processingDate?: Date;
  locationCode?: string;
  processedBy?: string;
  notes?: string;
}

// Bulk direct receipt (UX-A) — én forespørsel for N serial-linjer
export interface BulkDirectReceiptPlacement {
  lineId: number;
  locationCode: string;
  quantity?: number;
  itemBatchNo?: string;
  itemBatchExpDate?: string;
}

export interface BulkDirectReceiptRequest {
  placements: BulkDirectReceiptPlacement[];
  notes?: string;
}

export interface BulkDirectReceiptLineResult {
  lineId: number;
  success: boolean;
  locationCode: string;
  quantityPlaced: number;
  serialNo?: string;
  batchNo?: string;
  errorMessage?: string;
  errorCode?: string;
}

export interface BulkDirectReceiptResponse {
  success: boolean;
  totalRequested: number;
  totalPlaced: number;
  results: BulkDirectReceiptLineResult[];
  errorMessage?: string;
  processedAt: string;
  /** Etterkontroll (36333932): eksporten ble holdt tilbake — mottaket ligger i kontrollkøen. */
  heldForReview?: boolean;
  /** Antall eksportfiler skrevet (én per innkjøpsordre). 0 ved hold, feil eller bilmottak. */
  exportedFileCount?: number;
}

// Direkte utlegg fra ordrelinje (Norlux P19/P20) — legg varer rett ut på lokasjon
// uten ankomstregistrering. Samme lineNo kan forekomme flere ganger (split over lokasjoner).
export interface DirectPlacementItem {
  lineNo: number;
  itemNo: string;
  locationCode: string;
  quantity: number;
  warehouse?: string;
  itemBatchNo?: string;
  itemBatchExpDate?: string;
}

export interface DirectPlacementRequest {
  placements: DirectPlacementItem[];
  notes?: string;
  processedBy?: string;
}

export interface DirectPlacementLineResult {
  lineNo: number;
  itemNo: string;
  locationCode: string;
  quantityPlaced: number;
  success: boolean;
  errorMessage?: string;
  errorCode?: string;
}

export interface DirectPlacementResponse {
  success: boolean;
  totalRequested: number;
  totalPlaced: number;
  results: DirectPlacementLineResult[];
  exportedFiles: string[];
  errorMessage?: string;
  processedAt: string;
}

class DirectReceiptService {
  
  /**
   * Prosesserer direkte mottak for en StcTrans oppføring
   */
  async processDirectReceipt(
    batchId: string, 
    lineId: number, 
    request: DirectReceiptRequest
  ): Promise<DirectReceiptResponse> {
    const encodedBatchId = encodeURIComponent(batchId);
    
    try {
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/${lineId}/direct-receipt`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to process direct receipt'));
      }
      
      return await response.json();
    } catch (error) {
      console.error('Detaljert feil ved direkte mottak:', error);
      console.error('Request data:', request);
      throw error;
    }
  }

  /**
   * UX-A — Bulk direkte mottak: én forespørsel for N serial-/batch-linjer.
   * Brukes når operatør har flere serial-linjer i samme mottak og vil
   * plassere alle uten å gjenta single-line-dialogen.
   */
  async processBulkDirectReceipt(
    batchId: string,
    request: BulkDirectReceiptRequest
  ): Promise<BulkDirectReceiptResponse> {
    const encodedBatchId = encodeURIComponent(batchId);

    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/direct-receipt/bulk`,
      request
    );

    // Endepunktet returnerer alltid BulkDirectReceiptResponse-shape, både ved 200 og 400.
    // Parser body uansett status så klienten kan vise per-linje-resultater.
    try {
      const body = await response.json();
      // Typisert avvisning fra ExceptionHandlingMiddleware har ikke response-shapen
      // (ingen success/errorMessage) — løft teksten fra detail inn i errorMessage.
      if (!response.ok && body?.success === undefined) {
        return {
          success: false,
          totalRequested: request.placements.length,
          totalPlaced: 0,
          results: [],
          errorMessage: extractApiError(body, response),
          processedAt: new Date().toISOString()
        };
      }
      return body as BulkDirectReceiptResponse;
    } catch (parseErr) {
      // Fallback hvis respons ikke er gyldig JSON (skjer typisk ved 500)
      return {
        success: false,
        totalRequested: request.placements.length,
        totalPlaced: 0,
        results: [],
        errorMessage: `HTTP ${response.status} ${response.statusText}`,
        processedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Norlux P19/P20 — Legg varer direkte ut på lokasjon fra ordrelinje uten ankomstregistrering.
   * Ett atomisk kall som plasserer alle valgte linjer (og evt. split over lokasjoner).
   */
  async processDirectPlacement(
    orderNo: number,
    request: DirectPlacementRequest
  ): Promise<DirectPlacementResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stocktrans/orders/${orderNo}/direct-placement`,
      request
    );

    // Endepunktet returnerer alltid DirectPlacementResponse-shape (200 og 400).
    try {
      const body = await response.json();
      // Typisert avvisning fra ExceptionHandlingMiddleware — se processBulkDirectReceipt.
      if (!response.ok && body?.success === undefined) {
        return {
          success: false,
          totalRequested: request.placements.length,
          totalPlaced: 0,
          results: [],
          exportedFiles: [],
          errorMessage: extractApiError(body, response),
          processedAt: new Date().toISOString()
        };
      }
      return body as DirectPlacementResponse;
    } catch (parseErr) {
      return {
        success: false,
        totalRequested: request.placements.length,
        totalPlaced: 0,
        results: [],
        exportedFiles: [],
        errorMessage: `HTTP ${response.status} ${response.statusText}`,
        processedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Henter status for direkte mottak
   */
  async getDirectReceiptStatus(
    batchId: string, 
    lineId: number
  ): Promise<DirectReceiptStatusResponse> {
    const encodedBatchId = encodeURIComponent(batchId);
    
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/${lineId}/direct-receipt/status`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get direct receipt status: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting direct receipt status:', error);
      throw error;
    }
  }

  /**
   * Sjekker om direkte mottak er mulig for en StcTrans oppføring
   */
  async canProcessDirectReceipt(
    batchId: string, 
    lineId: number
  ): Promise<{ canProcess: boolean; message: string }> {
    try {
      const status = await this.getDirectReceiptStatus(batchId, lineId);
      
      if (status.isProcessed) {
        return {
          canProcess: false,
          message: 'Direct receipt has already been processed for this line'
        };
      }
      
      return {
        canProcess: true,
        message: 'Direct receipt can be processed'
      };
    } catch (error) {
      console.error('Error checking direct receipt eligibility:', error);
      return {
        canProcess: false,
        message: 'Unable to determine if direct receipt can be processed'
      };
    }
  }
}

export const directReceiptService = new DirectReceiptService();
